import React from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../data-table";
import ProjectProgressBar from "../project-management/ProjectProgressBar";
import DashboardPanel from "./DashboardPanel";

const REASON_STYLES = {
  overdue: "bg-red-50 text-red-700 border-red-200 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/30",
  dueSoon: "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-500/15 dark:text-orange-300 dark:border-orange-500/30",
  behind: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/30",
  ok: "bg-green-50 text-green-700 border-green-200 dark:bg-green-500/15 dark:text-green-300 dark:border-green-500/30",
};

const formatDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "2-digit", year: "numeric" }).format(date);
};

const TABLE_LABELS = { empty: "You're not assigned to any active projects." };

const columns = [
  {
    key: "projectName",
    label: "Project",
    sortable: true,
    searchable: true,
    render: (value, project) => (
      <div className="min-w-0">
        <div className="truncate font-medium text-app-primary">{value}</div>
        <div className="truncate text-xs text-app-muted">{project.projectType}</div>
      </div>
    ),
  },
  {
    key: "reason",
    label: "Status",
    sortable: true,
    sortValue: (project) => project.reason?.rank ?? 0,
    render: (reason) => (
      <span
        className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${REASON_STYLES[reason?.kind || "ok"]}`}
      >
        {reason?.label || (reason === null ? "On track" : "—")}
      </span>
    ),
  },
  {
    key: "percentage",
    label: "Progress",
    sortable: true,
    width: "170px",
    render: (percentage, project) => (
      <div>
        <div className="mb-1 flex items-center justify-between text-[11px] font-semibold text-app-secondary">
          <span>{`${project.totalRecruitsCount || 0}/${project.totalTasks || 0}`}</span>
          <span>{`${percentage}%`}</span>
        </div>
        <ProjectProgressBar percentage={percentage} />
      </div>
    ),
  },
  { key: "dueDate", label: "Due", sortable: true, render: formatDate },
];

/** Every active project the signed-in user is assigned to, most urgent first. */
function MyProjectsTable({ projects = [], loading = false, error = false, scopedToUser = true }) {
  const navigate = useNavigate();

  return (
    <DashboardPanel
      title="My Projects"
      description={
        scopedToUser
          ? "Active projects you're assigned to, most urgent first."
          : "Active projects visible to you, most urgent first."
      }
      action={{ label: "All projects", to: "/admin/project-management" }}
    >
      <DataTable
        ariaLabel="My projects"
        columns={columns}
        data={projects}
        loading={loading}
        error={error ? "Couldn't load projects." : undefined}
        pagination
        defaultPageSize={5}
        pageSizeOptions={[5, 10, 20]}
        onRowClick={(project) =>
          navigate(`/admin/project-management/${project.backendId || project.projectId}`, {
            state: { project },
          })
        }
        labels={TABLE_LABELS}
      />
    </DashboardPanel>
  );
}

export default MyProjectsTable;
